import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, ArrowRight, Download, FileType2, Loader2, MoreVertical, Pencil, Copy,
  History, Trash2, Check, X, RotateCcw, Package, CircleDashed,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader, ProvenanceBadge } from "@/components/Common";
import { api } from "@/lib/api";
import { downloadMarkdown, downloadServerFile } from "@/lib/exporter";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function MyBusiness() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [versions, setVersions] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const load = () => api.get("/assets").then(({ data }) => setData(data)).catch(() => setData({ assets: [], incomplete: [] }));
  useEffect(() => { load(); }, []);

  const runExport = async (fmt) => {
    setExporting(fmt);
    try {
      if (fmt === "md") downloadMarkdown(data.business, data.assets);
      else await downloadServerFile(`/export/${fmt}`, `${(data.business?.name || "my-business")}.${fmt}`);
      track("Assets Exported", { format: fmt, scope: "portfolio" });
      toast.success("Downloaded. Your portfolio, in a file you own.");
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(null); }
  };

  const exportAsset = async (a, fmt) => {
    setBusyId(a.asset_id);
    try {
      await downloadServerFile(`/export/asset/${a.asset_id}.${fmt}`, `${(a.title || a.name)}.${fmt}`);
      track("Assets Exported", { format: fmt, scope: "single" });
    } catch { toast.error("Download failed."); }
    finally { setBusyId(null); }
  };

  const startRename = (a) => { setRenaming(a.asset_id); setRenameVal(a.title || a.name); };
  const saveRename = async (a) => {
    const title = renameVal.trim();
    if (!title) return;
    try { await api.patch(`/assets/${a.asset_id}`, { title }); setRenaming(null); load(); toast.success("Renamed."); }
    catch { toast.error("Rename failed."); }
  };

  const duplicate = async (a) => {
    setBusyId(a.asset_id);
    try { await api.post(`/assets/${a.asset_id}/duplicate`); toast.success("Duplicated."); await load(); }
    catch { toast.error("Duplicate failed."); }
    finally { setBusyId(null); }
  };

  const openHistory = async (a) => {
    setHistoryFor(a); setVersions(null);
    try { const { data } = await api.get(`/assets/${a.asset_id}/versions`); setVersions(data.versions); }
    catch { setVersions([]); }
  };

  const restore = async (index) => {
    try { await api.post(`/assets/${historyFor.asset_id}/restore`, { index }); toast.success("Version restored."); setHistoryFor(null); load(); }
    catch { toast.error("Restore failed."); }
  };

  const doDelete = async () => {
    const a = deleteFor;
    try { await api.delete(`/assets/${a.asset_id}`); setDeleteFor(null); load(); toast.success("Asset deleted."); }
    catch { toast.error("Delete failed."); }
  };

  if (!data) return <FullPageLoader label="Loading your business…" />;

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="my-business">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{data.business?.name || "My business"}</h1>
            <p className="mt-1 text-slate-500">Every asset you've built, in one place. This is your business, in writing, and it's yours to keep.</p>
          </div>
          {data.assets.length > 0 && (
            <div className="flex flex-col items-end gap-2" data-testid="export-actions">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-muted">Business Launch Portfolio</p>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="export-pdf-btn" onClick={() => runExport("pdf")} disabled={!!exporting}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand px-3.5 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
                  {exporting === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <Package size={15} />} Portfolio PDF
                </button>
                <button data-testid="export-docx-btn" onClick={() => runExport("docx")} disabled={!!exporting}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-line bg-white px-3.5 py-2 text-sm font-medium text-brand-text transition-all duration-200 hover:bg-brand-surface disabled:opacity-50">
                  {exporting === "docx" ? <Loader2 size={15} className="animate-spin" /> : <FileType2 size={15} />} Word
                </button>
                <button data-testid="export-markdown-btn" onClick={() => runExport("md")} disabled={!!exporting}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-line bg-white px-3.5 py-2 text-sm font-medium text-slate-600 transition-all duration-200 hover:bg-slate-50 disabled:opacity-50">
                  <FileText size={15} /> Markdown
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Incomplete assets */}
        {data.incomplete?.length > 0 && (
          <div className="mt-8" data-testid="incomplete-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">In progress</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {data.incomplete.map((t) => (
                <button key={t.task_slug} data-testid={`incomplete-${t.task_slug}`} onClick={() => navigate(`/task/${t.task_slug}`)}
                  className="flex flex-col gap-2.5 rounded-xl border border-dashed border-brand-line bg-white p-4 text-left transition-all duration-200 hover:border-brand-muted">
                  <span className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2.5">
                      <CircleDashed size={16} className="text-amber-500" />
                      <span><span className="block text-sm font-medium">{t.asset_name}</span><span className="text-xs text-slate-500">{t.title}</span></span>
                    </span>
                    <span className="text-xs font-semibold text-slate-500" data-testid={`incomplete-completion-${t.task_slug}`}>{t.completion ?? 0}%</span>
                  </span>
                  <span className="block h-1.5 w-full overflow-hidden rounded-full bg-brand-surface">
                    <span className="block h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${t.completion ?? 0}%` }} />
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-brand">Continue <ArrowRight size={13} /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {data.assets.length === 0 ? (
          <div className="mt-8 rounded-xl border border-dashed border-brand-line bg-white p-10 text-center">
            <FileText className="mx-auto text-brand-muted" size={26} />
            <p className="mt-3 font-medium">No assets yet</p>
            <p className="mt-1 text-sm text-slate-500">Complete your first step and it'll appear here.</p>
            <button onClick={() => navigate("/dashboard")} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white">
              Go to today's focus <ArrowRight size={15} />
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Approved assets</p>
            {data.assets.map((a) => (
              <div key={a.asset_id} className="rounded-xl border border-brand-line bg-white p-6 shadow-soft" data-testid={`asset-${a.task_slug}`}>
                <div className="flex items-center justify-between gap-3">
                  {renaming === a.asset_id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <input data-testid="rename-input" autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveRename(a)}
                        className="flex-1 rounded-lg border border-brand focus:outline-none px-3 py-1.5 text-sm" />
                      <button data-testid="rename-save" onClick={() => saveRename(a)} className="rounded-md bg-brand p-1.5 text-white"><Check size={15} /></button>
                      <button onClick={() => setRenaming(null)} className="rounded-md border border-brand-line p-1.5 text-slate-500"><X size={15} /></button>
                    </div>
                  ) : (
                    <h2 className="font-heading text-lg font-medium">{a.title || a.name}
                      {a.duplicated_from && <span className="ml-2 rounded-full bg-brand-surface px-2 py-0.5 text-[10px] font-medium text-brand-text">copy</span>}
                      {a.version_count > 0 && <span className="ml-2 text-xs font-normal text-slate-400">v{a.version_count + 1}</span>}
                    </h2>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`asset-menu-${a.task_slug}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-700" disabled={busyId === a.asset_id}>
                        {busyId === a.asset_id ? <Loader2 size={16} className="animate-spin" /> : <MoreVertical size={16} />}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem data-testid={`action-edit-${a.task_slug}`} onClick={() => navigate(`/task/${a.task_slug}`)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-rename-${a.task_slug}`} onClick={() => startRename(a)}><Pencil size={14} className="mr-2" /> Rename</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-duplicate-${a.task_slug}`} onClick={() => duplicate(a)}><Copy size={14} className="mr-2" /> Duplicate</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`action-pdf-${a.task_slug}`} onClick={() => exportAsset(a, "pdf")}><Download size={14} className="mr-2" /> Download PDF</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-docx-${a.task_slug}`} onClick={() => exportAsset(a, "docx")}><FileType2 size={14} className="mr-2" /> Download Word</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-history-${a.task_slug}`} onClick={() => openHistory(a)} disabled={!a.version_count}><History size={14} className="mr-2" /> Version history{a.version_count ? ` (${a.version_count})` : ""}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`action-delete-${a.task_slug}`} onClick={() => setDeleteFor(a)} className="text-red-600 focus:text-red-600"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-3 flex items-center gap-3" data-testid={`asset-completion-${a.task_slug}`}>
                  <span className="block h-1.5 flex-1 overflow-hidden rounded-full bg-brand-surface">
                    <span className="block h-full w-full rounded-full bg-emerald-500" />
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-emerald-600"><Check size={13} /> 100% complete</span>
                </div>
                <div className="mt-4 space-y-4">
                  {a.blocks.map((b) => (
                    <div key={b.key}>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{b.label}</p>
                        <ProvenanceBadge provenance={b.provenance} />
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{b.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Version history */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent data-testid="version-history-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>{historyFor?.title || historyFor?.name} · restore any earlier version. Your current version is saved before restoring.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-2 overflow-auto">
            {versions === null ? (
              <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin text-brand" size={20} /></div>
            ) : versions.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">No earlier versions yet. Editing a completed asset saves a version here.</p>
            ) : (
              versions.slice().reverse().map((v) => (
                <div key={v.index} data-testid={`version-${v.index}`} className="flex items-center justify-between rounded-lg border border-brand-line p-3">
                  <div>
                    <p className="text-sm font-medium">Version {v.index + 1}</p>
                    <p className="text-xs text-slate-400">{v.saved_at ? new Date(v.saved_at).toLocaleString() : ""}</p>
                  </div>
                  <button data-testid={`restore-${v.index}`} onClick={() => restore(v.index)} className="inline-flex items-center gap-1.5 rounded-md border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-text hover:bg-brand-surface">
                    <RotateCcw size={13} /> Restore
                  </button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent data-testid="delete-asset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteFor?.title || deleteFor?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes the asset from your business. You can rebuild it by completing the step again. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-asset-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-asset-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
